import React, { memo } from 'react';
import { View } from 'react-native';

import Text from '../../../components/text/Text';
import { ActionDock, Card, EmptyState, MachineChip, Tag } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useDriverDay } from '../DriverDayProvider';
import useDriverDayActions from '../useDriverDayActions';
import StartOfDayStepScreen from './StartOfDayStepScreen';
import useStartOfDayStep from './useStartOfDayStep';

const countLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

const MachineTicketsCard = memo(({ row, onOpen }) => {
	const styles = useThemedStyles(createStyles);
	const { docket, stopName, ticketCount, descriptions } = row;
	const placeName = docket.machine?.place?.name;
	const siteName = docket.machine?.location?.name;

	return (
		<Card
			tone="raised"
			onPress={() => onOpen(docket)}
			accessibilityLabel={`${stopName}, ${countLabel(ticketCount, 'ticket', 'tickets')}`}
			accessibilityHint="Opens the tickets for this machine"
			style={styles.card}
		>
			<View style={styles.cardHeader}>
				<View style={styles.cardTitles}>
					<Text style={styles.site}>{siteName || placeName || stopName}</Text>
					<View style={styles.positionRow}>
						{Boolean(placeName && siteName) && <Text style={styles.position}>{placeName}</Text>}
						<MachineChip number={docket.machine?.assetId} />
					</View>
				</View>
				<Tag label={countLabel(ticketCount, 'ticket', 'tickets')} />
			</View>
			{descriptions.map((description, index) => (
				<View key={index} style={styles.ticket}>
					<View style={styles.bullet} />
					<Text style={styles.ticketText}>{description}</Text>
				</View>
			))}
		</Card>
	);
});

/**
 * Tickets summary as a start of day step (TicketSummaryView with
 * `startOfDayStep`): today's driver tickets grouped by machine, site first;
 * a machine opens its ticket list; "Tickets read and packed for" confirms.
 */
const TicketsSummaryStep = () => {
	const day = useDriverDay();
	const { ticketRows, trainingOffline } = day;
	const { isDone, stepLabel, afterThis } = useStartOfDayStep(day, 'ticketsSummary');
	const { openTickets, confirmStepByKey, isConfirmingStep } = useDriverDayActions(day);
	const ticketCount = ticketRows.reduce((sum, row) => sum + row.ticketCount, 0);

	return (
		<StartOfDayStepScreen
			day={day}
			stepLabel={stepLabel}
			heading="Tickets summary"
			subheading={ticketRows.length
				? `${countLabel(ticketCount, 'ticket', 'tickets')} across ${countLabel(ticketRows.length, 'machine', 'machines')}. Read them and pack anything you need.`
				: undefined}
			dock={isDone ? (
				<ActionDock hint="Tickets read and packed for." />
			) : (
				<ActionDock
					hint={afterThis}
					action={{
						label: 'Tickets read and packed for',
						onPress: () => confirmStepByKey('ticketsSummary'),
						loading: isConfirmingStep,
						disabled: trainingOffline || !ticketRows.length,
					}}
				/>
			)}
		>
			{ticketRows.map(row => (
				<MachineTicketsCard key={row.docket.id} row={row} onOpen={openTickets} />
			))}
			{!ticketRows.length && (
				<EmptyState title="No tickets today" message="No machine on this run has tickets for you." />
			)}
		</StartOfDayStepScreen>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	card: {
		gap: space.sm,
		paddingVertical: 14,
	},
	cardHeader: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: space.md,
	},
	cardTitles: {
		flex: 1,
		gap: space.xs,
	},
	site: {
		...type.rowTitleSemibold,
		color: colors.text,
	},
	positionRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: space.sm,
	},
	position: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	ticket: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 10,
	},
	bullet: {
		width: 6,
		height: 6,
		marginTop: 7,
		borderRadius: radius.pill,
		backgroundColor: colors.accent,
	},
	ticketText: {
		...type.bodySmall,
		color: colors.text,
		flex: 1,
		// Lets long links wrap inside the card on web instead of widening the row.
		minWidth: 0,
	},
});

export default TicketsSummaryStep;
